import { Injectable } from "@angular/core";
import { HttpClient, HttpEventType, HttpResponse } from "@angular/common/http";
import { HttpHelperService } from "./httphelper.service";
import { environment } from "../../../environments/environment";
import { ManualEvent, ManualEventType } from "../model/manual-event";
import { Observable, throwError } from "rxjs";
import { catchError, filter, map, tap } from "rxjs/operators";
import { BehaviorSubject } from 'rxjs';
import { CarehomeSettingsService } from "./carehome-settings.service";

export const BASE_API_URL = `${environment.apiUrl}manualevent`;

@Injectable({
	providedIn: 'root',
})
export class ManualEventService {
	private manualEventTypesSubject = new BehaviorSubject<ManualEventType[]>([]);

	constructor(
		private http: HttpClient,
		private httpHelperService: HttpHelperService,
		private carehomeSettingsService: CarehomeSettingsService) { }


	submitManualEvent(residentId: string, eventType: number) {
		const body = {
			ResidentId: residentId,
			EventType: eventType,
		};

		return this.http.post(`${BASE_API_URL}/resident/${residentId}`, body, this.httpHelperService.getHttpOptions());
	}

	getManualEventsByResident(residentId: string, month?: number, year?: number): Observable<ManualEvent[]> {
		let url = `${BASE_API_URL}/resident/${residentId}`;

		const params: string[] = [];
		if (month !== undefined) {
			params.push(`month=${month}`);
		}
		if (year !== undefined) {
			params.push(`year=${year}`);
		}

		if (params.length > 0) {
			url += `?${params.join("&")}`;
		}

		return this.http.get<ManualEvent[]>(url, this.httpHelperService.getHttpOptions()).pipe(
			map((response: any) => response.body as ManualEvent[])
		);
	}

	/** Manual events between two absolute instants - `from` inclusive, `to` exclusive. */
	getManualEventsByResidentInRange(residentId: string, from: Date, to: Date): Observable<ManualEvent[]> {
		const url = `${BASE_API_URL}/resident/${residentId}`
			+ `?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`;

		return this.http.get<ManualEvent[]>(url, this.httpHelperService.getHttpOptions()).pipe(
			map((response: any) => response.body as ManualEvent[])
		);
	}

	//#region Upload CSV File
	uploadCsvFile(file: File): Observable<{ UploadedEvents: number; FailedEvents: number }> {
		const formData = new FormData();
		formData.append("file", file);
		const options = this.httpHelperService.getHttpOptionsWithMultiPart();
		return this.http
			.post<{ UploadedEvents: number; FailedEvents: number }>(
				`${BASE_API_URL}/uploadCsv`,
				formData,
				options
			)
			.pipe(
				filter((event: any) => event.type === HttpEventType.Response),
				map((event: any) => {
					const result = event.body as { UploadedEvents: number; FailedEvents: number };
					this.manualEventTypesSubject.next([]); // Invalidate cache
					return result;
				}),
				catchError((error) => throwError(error))
			);
	}
	//#endregion

	//#region Manual Event Types Management
	getManualEventTypes(): Observable<ManualEventType[]> {
		const currentValue = this.manualEventTypesSubject.getValue();
		if (currentValue && currentValue.length > 0) {
			return this.manualEventTypesSubject.asObservable();
		}

		return this.http
			.get<ManualEventType[]>(`${BASE_API_URL}`, this.httpHelperService.getHttpOptions())
			.pipe(
				map((response) => {
					const manualEventTypes = response.body as ManualEventType[];
					this.manualEventTypesSubject.next(manualEventTypes); // Update the subject
					return manualEventTypes;
				}),
				catchError((error) => throwError(error))
			);
	}

	addManualEventType(eventType: ManualEventType): Observable<ManualEventType> {
		return this.http
			.post<ManualEventType>(`${BASE_API_URL}`, eventType, this.httpHelperService.getHttpOptions())
			.pipe(
				map((response) => {
					const newEventType = response.body as ManualEventType;
					const currentList = this.manualEventTypesSubject.getValue();
					this.manualEventTypesSubject.next([...currentList, newEventType]); // Update the subject
					return newEventType;
				}),
				catchError((error) => throwError(error))
			);
	}

	updateManualEventType(event: ManualEventType): Observable<ManualEventType> {
		return this.http.put<ManualEventType>(
			`${BASE_API_URL}/${event.id}`,
			event,
			this.httpHelperService.getHttpOptions()
		).pipe(
			map((response: any) => response.body as ManualEventType) // Map the response to ManualEventType
		);
	}

	deleteManualEventType(eventId: number): Observable<void> {
		return this.http
			.delete<void>(`${BASE_API_URL}/${eventId}`, this.httpHelperService.getHttpOptions())
			.pipe(
				map(() => {
					const currentList = this.manualEventTypesSubject.getValue();
					const updatedList = currentList.filter(event => event.id !== eventId);
					this.manualEventTypesSubject.next(updatedList); // Update the subject
				}),
				catchError((error) => throwError(error))
			);
	}
	//#endregion

	//#region Base API Methods
	getAccessibleManualEventTypes(): Observable<ManualEventType[]> {
		return this.http
			.get<ManualEventType[]>(`${BASE_API_URL}/carehome`, this.httpHelperService.getHttpOptions())
			.pipe(
				map((response) => response.body as ManualEventType[]),
				catchError((error) => throwError(error))
			);
	}

	updateAccessibleManualEventTypes(numbers: number[]): Observable<number[]> {
		return this.http
			.post<number[]>(`${BASE_API_URL}/carehome`, numbers, this.httpHelperService.getHttpOptions())
			.pipe(
				map((response) => response.body as number[]),
				tap(() => {
					this.carehomeSettingsService.clearLocalSettings();
				}),
				catchError((error) => throwError(error))
			);
	}
	//#endregion
}
